<template>
  <div class="flex flex-col flex-1 justify-start min-w-[320px]">
    <div v-if="loading" class="fixed inset-0 flex justify-center items-center z-50">
      <div class="fixed inset-0 bg-white bg-opacity-50 backdrop-blur-sm"></div>
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
    </div>
    <div class="flex flex-col items-center mt-2">
      <span class="text-2xl font-light">{{store.tableState.name}}</span>
      <span class="text-base text-gray-400">
        ({{ store.tableState.rowCount }} events, {{ totalMarkets }} markets)
      </span>
    </div>
    <div class="flex justify-between mb-4 mt-2">
      <!-- Settings, Watchlist Subscription, buttons on the left, Sort button on the right -->
      <div class="flex gap-2 pl-2">
        <EllipsisMenu :actions="[{
            label: this.currentWatchList?.tableSettings?.showClosedItems ? 'Hide Closed Items' : 'Show Closed Items',
            icon: this.currentWatchList?.tableSettings?.showClosedItems ? 'fas fa-eye-slash' : 'fas fa-eye',
            onClick: () => toggleShowClosedItems()
          },
          {
            label: 'Delete Watchlist',
            icon: 'far fa-trash-alt',
            textColor: 'text-red-600',
            onClick: () => deleteWatchList()
          }]" />

        <!-- Watchlist Subscription Button -->
        <button 
          @click="setTrendAlert(SubscriptionType.WATCHLIST, {
            watchlistId: currentWatchList.id,
            targetTitle: currentWatchList.name

          })"
          class="text-gray-600 hover:text-gray-800 border rounded-md border-gray-300 shadow-md px-2">
          + <i class="fas fa-bell"></i>
        </button>
      </div>
    

      <!-- Sort Menu -->
       <div class="flex pr-2">
        <EllipsisMenu 
          :actions="sortOptions.map(option => ({
            label: `${option.label}${selectedSort.key === option.key && selectedSort.order === option.order ? ' ☑️' : ''}`,
            icon: option.order === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down',
            onClick: () => selectedSort = option,
            selected: selectedSort.key === option.key && selectedSort.order === option.order
          }))"
          menuSvg="up-down-arrow"
        />
      </div>
    </div>

    <div class="flex flex-row flex-wrap">
      <div v-for="event in groupedEvents" 
           :key="event.id" 
           class="border rounded-lg shadow-md mb-4 first:mt-4 bg-white"
         v-show="!event.closed || currentWatchList?.tableSettings?.showClosedItems">
         
        <!-- Single market event -->
        <div v-if="event.markets.length === 1" class="p-2 max-w-[320px]">
          <MarketChart :event="event" :market="event.markets[0]" />
          <div class="flex justify-between">
            <EllipsisMenu :actions="[{
              label: 'Remove Event',
              textColor: 'text-red-600',
              icon: 'far fa-trash-alt',
              onClick: () => removeFromList(event)
            },{
              label: 'Remove Event',
              textColor: 'text-red-600',
              icon: 'far fa-trash-alt',
              onClick: () => removeFromList(event)
            },{
              label: 'Remove Event',
              textColor: 'text-red-600',
              icon: 'far fa-trash-alt',
              onClick: () => removeFromList(event)
            }]" />
            <button 
              @click="setTrendAlert(SubscriptionType.MARKET, {
                targetTitle: event.markets[0].question,
                watchlistId: currentWatchList.id,
                eventId: event.id,
                marketId: event.markets[0].id,
                clobTokenId: parseMarketClobTokenId(event.markets[0])?.[0]
              })"
              class="text-gray-600 hover:text-gray-800 border rounded-md border-gray-300 shadow-md px-2 z-10">
              + <i class="fas fa-bell"></i>
            </button>
          </div>
        </div>

        <!-- Multiple markets event -->
        <Disclosure v-else v-slot="{ open }" 
                  as="div" 
                  ref="disclosures"
                  :defaultOpen="false"
                  class="relative max-w-[320px]">

          <DisclosureButton 
            class="w-full bg-white" 
            :class="{ 'sticky top-0 z-10': open }">
            <div class="flex flex-col items-center justify-between pt-2 px-2 hover:bg-gray-50">
              <span class="text-base font-semibold font-mono">
                {{ event.title }}
                <span v-if="event.closed && !currentWatchList?.tableSettings?.showClosedItems" class="text-red-800">[CLOSED]</span>
              </span>
              <span class="text-xs font-mono pt-2">
                {{ event.markets.length }} markets |  Total Volume: <span class="font-bold text-teal-600">{{ formattedVolume(event) }}</span>
              </span>
            </div>
            <div class="flex justify-between items-center px-2 pt-4 mb-2">
              <EllipsisMenu :actions="[{
                label: 'Remove Event',
                icon: 'far fa-trash-alt',
                textColor: 'text-red-600',
                onClick: () => removeFromList(event)
              }]" />
              <i class="fas fa-chevron-up text-gray-600 transition-transform duration-200"
                :class="{ 'rotate-180 transform': !open }">
              </i>
              <button 
                @click.stop="setTrendAlert(SubscriptionType.EVENT, {
                  eventId: event.id,
                  targetTitle: event.title,
                  watchlistId: currentWatchList.id
                })"
                class="text-gray-600 hover:text-gray-800 border rounded-md border-gray-300 shadow-md px-2 z-10">
                + <i class="fas fa-bell"></i>
              </button>
            </div>
          </DisclosureButton>
          
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <DisclosurePanel>
              <div class="p-0">
                <div v-for="market in event.markets" 
                    class="p-2 mb-2 border rounded-lg shadow-md"
                    :key="market.id"
                    v-show="shouldShowMarket(event, market)">
                  <MarketChart :event="event" :market="market" />
                  <div class="flex justify-between">
                    <EllipsisMenu :actions="[{
                      label: 'Remove Market',
                      icon: 'far fa-trash-alt',
                      onClick: () => removeMarketFromEvent(event.id, market.id)
                    }]" />
                    <button 
                      @click="setTrendAlert(SubscriptionType.MARKET, {
                        watchlistId: currentWatchList.id,
                        eventId: event.id,
                        marketId: market.id,
                        targetTitle: market.question,
                        clobTokenId: parseMarketClobTokenId(market)?.[0]
                      })"
                      class="text-gray-600 hover:text-gray-800 border rounded-md border-gray-300 shadow-md px-2">
                      + <i class="fas fa-bell"></i>
                    </button>
                  </div>
                </div>
                <div class="flex justify-between">
                  <button class="text-gray-600 hover:text-gray-800 border rounded-md border-gray-300 shadow-md px-2 invisible">
                    <i class="fas fa-ellipsis-h"></i>
                  </button>
                  <DisclosureButton class="hover:bg-gray-100 rounded-full p-2">
                    <i class="fas fa-chevron-up text-gray-600"></i>
                  </DisclosureButton>
                  <button class="text-gray-600 hover:text-gray-800 border rounded-md border-gray-300 shadow-md px-2 invisible">
                    + <i class="fas fa-bell"></i>
                  </button>
                </div>
              </div>
            </DisclosurePanel>
          </transition>
        </Disclosure>
      </div>
    </div>

    <!-- Add SubscriptionsMenu component -->
    <SubscriptionsDialogContainer 
      ref="subscriptionsMenu"
      :is-open="showSubscriptionsDialog"
      :is-mobile="true"
      :scope="subscriptionScope"
      :target-title="subscriptionTargetTitle"
      :watchlist="currentWatchList"
      :events="subscriptionEvents"
      @close="showSubscriptionsDialog = false"
    />

    <!-- Add delete confirmation modal -->
    <TransitionRoot appear :show="isDeleteModalOpen" as="template">
      <Dialog as="div" @close="closeDeleteModal" class="relative z-50">
        <div class="fixed inset-0 bg-black/30" />
        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6">
              <DialogTitle class="text-lg font-medium mb-4">Confirm Deletion</DialogTitle>
              <p class="mb-6">Are you sure you want to delete this watchlist?</p>
              <div class="flex justify-end space-x-3">
                <button 
                  @click="closeDeleteModal"
                  class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button 
                  @click="confirmDelete"
                  class="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white"
                >
                  Delete
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { store, updateWatchList, deleteWatchList, showDialog, parseMarketClobTokenId } from '../store';
import MarketChart from './MarketChart.vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { size } from 'lodash';
import SubscriptionsDialogContainer from './SubscriptionsDialogContainer.vue';
import { SubscriptionType, SubscriptionScope } from '../types/Subscriptions';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from '@headlessui/vue'
import EllipsisMenu from './menus/EllipsisMenu.vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';


export default {
  name: 'EventTiles',
  
  components: {
    MarketChart,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    SubscriptionsDialogContainer,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    EllipsisMenu,
  },
  
  props: {
    loading: {
      type: Boolean,
      default: false
    },
  },

  computed: {
    events() {
      return this.store.tableState?.events?.filter(event => event) || [];
    },
    groupedEvents() {
      let events = [...this.events]; // Create a copy to sort
      
      // Sort based on selected sort option
      if (this.selectedSort.key) {
        events.sort((a, b) => {
          let valueA, valueB;
          
          switch(this.selectedSort.key) {
            case 'title':
              valueA = a.title || '';
              valueB = b.title || '';
              break;
            case 'volume':
              valueA = parseFloat(a.volume) || 0;
              valueB = parseFloat(b.volume) || 0;
              break;
            case 'createdAt':
              valueA = new Date(a.createdAt).getTime();
              valueB = new Date(b.createdAt).getTime();
              break;
            case 'endDate':
              valueA = new Date(a.endDate).getTime();
              valueB = new Date(b.endDate).getTime();
              break;
            default:
              return 0;
          }
          
          // Handle ascending/descending
          const modifier = this.selectedSort.order === 'asc' ? 1 : -1;
          
          if (valueA < valueB) return -1 * modifier;
          if (valueA > valueB) return 1 * modifier;
          return 0;
        });
      }
      
      // Group events (maintain existing grouping logic)
      const grouped = [];
      events.forEach(event => {
        if (!grouped.some(e => e.id === event.id)) {
          grouped.push(event);
        }
      });
      
      return grouped;
    },
    totalMarkets() {
      return this.events.reduce((total, event) => total + event.markets.length, 0);
    }
  },

  data() {
    const breakpoints = useBreakpoints(breakpointsTailwind);
    return {
      store,
      isMobile: breakpoints.smallerOrEqual('sm'),
      SubscriptionType,
      SubscriptionScope,
      subscriptionTargetTitle: '',
      subscriptionEvents: {},
      subscriptionEventId: '',
      subscriptionMarketId: '',
      subscriptionClobTokenId: '',
      subscriptionScope: SubscriptionScope.WATCHLIST,
      currentWatchList: {},
      subscriptionWatchlistId: '',
      subscriptionEvents: {},
      selectedSort: {
        key: null,
        order: null
      },
      sortOptions: [
        { key: 'title', label: 'Title', order: 'asc' },
        { key: 'title', label: 'Title', order: 'desc' },
        { key: 'volume', label: 'Volume', order: 'asc' },
        { key: 'volume', label: 'Volume', order: 'desc' },
        { key: 'createdAt', label: 'Created At', order: 'asc' },
        { key: 'createdAt', label: 'Created At', order: 'desc' },
        { key: 'endDate', label: 'End Date', order: 'asc' },
        { key: 'endDate', label: 'End Date', order: 'desc' }
      ],
      showClosedItems: false,
      showSubscriptionsDialog: false,
      isDeleteModalOpen: false,
    }
  },
  
  watch: {
    '$route.params': {
      handler(newParams, oldParams) {
        if (newParams.listId && newParams.listId !== oldParams?.listId) {
          this.currentWatchList = store.watchLists.find(item => item.id === newParams.listId);
        } else {
          this.currentWatchList = {};
        }
      },
      immediate: true
    },
    selectedSort: {
      handler(newSort) {
        // Reset all sort orders first
        Object.keys(store.tableState.sortOrder || {}).forEach(key => {
          store.tableState.sortOrder[key] = null;
        });
        
        // Set the new sort order if a valid sort is selected
        if (newSort.key && newSort.order) {
          if (!store.tableState.sortOrder) {
            store.tableState.sortOrder = {};
          }
          store.tableState.sortOrder[newSort.key] = newSort.order;
        }
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    parseMarketClobTokenId(market) {
      return parseMarketClobTokenId(market);
    },
    size(obj) {
      return size(obj);
    },
    formattedVolume(event) {
      let numericVolume = parseFloat(event.volume);
      if (isNaN(numericVolume)) {
        numericVolume = 0;
      }
      return numericVolume.toLocaleString('en-US', { 
        style: 'currency', 
        currency: 'USD', 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      });
    },
    getSortLabel() {
      const selectedOption = this.sortOptions.find(option => 
        option.key === this.selectedSort.key && 
        option.order === this.selectedSort.order
      );
      return selectedOption ? selectedOption.label : 'Sort by';
    },
    
    shouldShowMarket(event, market) {
      const eventSettings = this.currentWatchList?.events?.[event.id];
      if (!eventSettings) return true;

      // If the market is closed and the user has not selected to show closed items, don't show it
      if (market.closed && !this.currentWatchList?.tableSettings?.showClosedItems) {
        return false;
      }

      return true;
    },
    toggleExpandAll() {
      if (size(this.currentWatchList) > 0) {
        // Initialize tableSettings if it doesn't exist
        this.currentWatchList.tableSettings = this.currentWatchList.tableSettings || {showClosedItems: true, expandMultiMarketEventsTable: true, expandMultiMarketEventsTiles: false};
        // Toggle the expandMultiMarketEventsTiles setting
        this.currentWatchList.tableSettings.expandMultiMarketEventsTiles = !this.currentWatchList.tableSettings.expandMultiMarketEventsTiles;
        
        // Update settings through API
        updateWatchList(this.currentWatchList.id, {
          tableSettings: this.currentWatchList.tableSettings
        });
      }
    },
    async toggleShowClosedItems() {
      if (size(this.currentWatchList) > 0) {
        this.currentWatchList.tableSettings = this.currentWatchList.tableSettings || {showClosedItems: true, expandMultiMarketEventsTable: true, expandMultiMarketEventsTiles: false};
        this.currentWatchList.tableSettings.showClosedItems = !this.currentWatchList.tableSettings.showClosedItems;

        // Update the settings through the API
        await updateWatchList(this.currentWatchList.id, {
          tableSettings: this.currentWatchList.tableSettings
        });
      }
    },
    setTrendAlert(type, props = {}) {
      this.subscriptionScope = scope;

      switch (type) {
        case SubscriptionType.WATCHLIST:
          this.subscriptionTargetTitle = this.currentWatchList.name;
          this.subscriptionEvents = reduce(this.currentWatchList.events, (acc, event, eventId) => {
            acc[eventId] = pick(event, ['markets']);
            return acc;
          }, {});
          break;
        case SubscriptionType.EVENT:
          this.subscriptionTargetTitle = this.contextMenu.selectedEvent.title;
          this.subscriptionEvents[selectedEventId] = pick(this.currentWatchList.events[selectedEventId] || {
            markets: [],
          }, ['markets']);
          break;
        case SubscriptionType.MARKET:
          this.subscriptionTargetTitle = this.contextMenu.selectedMarket.question;
          this.subscriptionEvents[selectedEventId] = {
            markets: [selectedMarketId],
          };
          break;
      }

      this.showSubscriptionsDialog = true;
    },
    async removeFromList(event) {
      if (!this.currentWatchList) return;
      
      // Remove from watchlist
      if (this.currentWatchList.events) {
        delete this.currentWatchList.events[event.id];
      }
          
      // Update settings
      await updateWatchList(this.currentWatchList.id, this.currentWatchList);
      
      showDialog({
        message: `Removed event from ${this.currentWatchList.name}`,
        type: 'success',
        timeout: 3000
      });
    },
    deleteWatchList() {
      this.isDeleteModalOpen = true;
    },
    closeDeleteModal() {
      this.isDeleteModalOpen = false;
    },
    async confirmDelete() {
      if (this.currentWatchList) {
        await deleteWatchList(this.currentWatchList.id);
        this.$router.push('/');
      }
      this.closeDeleteModal();
    },
    removeMarketFromEvent(eventId, marketId) {
      // Initialize event structure if needed
      if (!this.currentWatchList.events) {
        console.warn('currentWatchList.events is empty, initializing');
        return;
      }
      if (!this.currentWatchList.events[eventId]) {
        console.warn('currentWatchList.events[',eventId,'] is empty');
        return;
      }

      // Initialize markets object if needed
      if (!this.currentWatchList.events[eventId].markets) {
        console.warn('currentWatchList.events[',eventId,'] markets is empty');
        return;
      }

      this.currentWatchList.events[eventId].autoFetch = false;
      this.currentWatchList.events[eventId].markets = this.currentWatchList.events[eventId].markets.filter(id => id !== marketId);

      if (this.currentWatchList.events[eventId].markets.length === 0) {
        delete this.currentWatchList.events[eventId];
      }

      // Update settings
      updateWatchList(this.currentWatchList.id, this.currentWatchList);
      
      showDialog({
        message: `Removed market from ${this.currentWatchList.name}`,
        type: 'success',
        timeout: 3000
      });
    },
  }
}
</script>

<style scoped>

</style> 